import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { session, loading } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-50 gap-4">
        <div className="w-9 h-9 rounded-full border-2 border-accent-300 border-t-accent-500 animate-spin"></div>
        <p className="text-sm text-foreground-500">Cargando…</p>
      </div>
    );
  }

  // Sin sesión de recuperación activa, no hay nada que hacer aquí.
  if (!session) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-background-200 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-alert-line text-amber-600 text-xl"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-900 mb-2">No hay sesión de recuperación activa</h2>
          <p className="text-sm text-foreground-500 mb-6">
            Para restablecer tu contraseña primero debes verificar el código de recuperación.
          </p>
          <Link
            to="/auth/forgot-password"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Ir a recuperar contraseña
          </Link>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');

    if (!isSupabaseConfigured || !supabase) {
      setError('La conexión con Supabase no está configurada.');
      return;
    }
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setSubmitting(true);
    const { error: err } = await supabase.auth.updateUser({ password });

    if (err) {
      setSubmitting(false);
      setError(err.message);
      return;
    }

    await supabase.auth.signOut();
    setSubmitting(false);
    navigate('/login', { replace: true, state: { resetSuccess: true } });
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Marca */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-accent-500 flex items-center justify-center mb-4">
            <i className="ri-heart-pulse-line text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 font-heading">Nueva contraseña</h1>
          <p className="text-sm text-foreground-500 mt-1">Paticas con Propósito · Panel de administración</p>
        </div>

        {/* Tarjeta */}
        <div className="bg-white rounded-2xl border border-background-200 p-8">
          <h2 className="text-lg font-semibold text-foreground-900">Establece tu contraseña</h2>
          <p className="text-sm text-foreground-500 mt-1 mb-6">
            Define una nueva contraseña para tu cuenta.
          </p>

          {error && (
            <div className="mb-5 flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Nueva contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full text-sm px-4 py-2.5 pr-11 rounded-lg border border-background-200 bg-background-50 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Confirmar contraseña
              </label>
              <input
                id="confirm"
                name="confirm"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repite la contraseña"
                className="w-full text-sm px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300 transition"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full text-sm font-semibold py-2.5 px-4 rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {submitting ? 'Guardando…' : 'Guardar contraseña'}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-foreground-500 hover:text-accent-600 transition-colors inline-flex items-center gap-1.5"
          >
            <i className="ri-arrow-left-line"></i>Volver al inicio de sesión
          </Link>
        </div>
      </div>
    </div>
  );
}